import { ExpandMore, NavigateNext } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Box, Tab, Tabs, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router-dom";
import CloudUploadSVG from "../../../../../assets/images/icons/CloudUpload";
import ExternalLink from "../../../../../assets/images/icons/ExternalLink.tsx";
import BriaButton from "../../../../../components/common/BriaButton/BriaButton";
import BriaSlider from "../../../../../components/common/BriaSlider/BriaSlider";
import { ModelDropdownKey } from "../../../../../components/common/DropDowns/TextToImageModelsDropdown/NewTextToImageModelsDropdown.tsx";
import ImageUploader from "../../../../../components/common/ImageUploader/ImageUploader";
import { ImageReferenceGalleryConstants } from "../../../../../constants/ImageReferenceGalleryConstants.ts";
import LinkConstants from "../../../../../constants/LinkConstants.ts";
import RouterConstants from "../../../../../constants/RouterConstants.ts";
import { IMAGE_REFERENCE_DISABLED_MODELS } from "../../../../../constants/TextToImageConstants.ts";
import { useAppStore } from "../../../../../hooks/useStores";
import InputLayout from "../../../../../layout/InputLayout/InputLayout";
import { ImageReferenceConfigsType } from "../../../../../models/text-to-image.ts";
import { isTGApp } from "../../../../../utils/index.ts";
import iframeStore from "../../../../IframeNew/iframe-store.tsx";
import { isLightVersion } from "../../../../NewTG/utils/tg-utils.ts";
import TextToImageError from "../../Generator/Search/Error/TextToImageError.tsx";
import ImageReferenceGallery from "./Examples/ImageReferenceGallery.tsx";
import styles from "./ImageReference.module.scss";

export enum IMAGE_REFERENCE_TYPES {
	STRUCTURE = "structure",
	PORTRAIT = "portrait",
}

type Props = {
	disabledMessage?: string;
};

const ImageReference = ({ disabledMessage = "" }: Props) => {
	const { t, i18n } = useTranslation("translation", {
		keyPrefix: "playground.textToImage.config.imageReference",
	});
	const inputRef = useRef<HTMLDivElement | null>(null);
	const { textToImageStore } = useAppStore();
	const [isImageGalleryOpen, setIsImageGalleryOpen] = useState<boolean>(false);
	const [isImageReferenceOpen, setIsImageReferenceOpen] = useState<boolean>(true);
	const [activeTab, setActiveTab] = useState(IMAGE_REFERENCE_TYPES.STRUCTURE);
	const isLight = isLightVersion(textToImageStore.config.training_version);
	const isDisabled = IMAGE_REFERENCE_DISABLED_MODELS.includes(
		(textToImageStore.config.model + "_" + textToImageStore.config.model_version) as ModelDropdownKey,
	);
	const hideError =
		i18n.exists(`playground.textToImage.errors.${textToImageStore.errors?.generate?.message}`) ||
		textToImageStore.IpSignalWarning;
	const tgProjectsLink = RouterConstants.NEW_TG.PROJECTS.fullPath;
	const imageReferenceTabs = [
		{
			name: IMAGE_REFERENCE_TYPES.STRUCTURE,
			title: t("referenceGallery"),
			subTitle: t("referenceGallerySubtitle"),
			images: [
				{
					title: t("imageGallery.titles.sketch_to_illustration"),
					images: ImageReferenceGalleryConstants.sketch_to_illustration,
				},
				{
					title: t("imageGallery.titles.photo_variations"),
					images: ImageReferenceGalleryConstants.photo_variations,
				},
				{
					title: t("imageGallery.titles.illustration_variations"),
					images: ImageReferenceGalleryConstants.illustration_variations,
				},
				{
					title: t("imageGallery.titles.typography_logo_variations"),
					images: ImageReferenceGalleryConstants.typography_logo_variations,
				},
			],
		},
		{
			name: IMAGE_REFERENCE_TYPES.PORTRAIT,
			title: t("portraitReferenceGallery"),
			subTitle: t("portraitReferenceGallerySubtitle", { tgProjectsLink }),
			images: [
				{
					title: t("imageGallery.titles.portrait"),
					description: t("imageGallery.descriptions.portrait"),
					images: ImageReferenceGalleryConstants.portrait,
				},
			],
		},
	];

	if (iframeStore.isIframe() && !iframeStore.iframe.config.image_generation_config?.enable_restyle_portraits) {
		imageReferenceTabs.pop();
	}

	const step: number = 0.05;
	const marks = Array.from({ length: Math.floor(1 / step) + 1 }, (_, i) => {
		const value = i * step;
		return {
			value,
			label: value === 0 ? t("loose") : value === 1 ? t("strict") : undefined,
		};
	});

	useEffect(() => {
		if (!isLight && activeTab === IMAGE_REFERENCE_TYPES.PORTRAIT) {
			setActiveTab(IMAGE_REFERENCE_TYPES.STRUCTURE);
		}
	}, [textToImageStore.config.tailored_model_id]);

	useEffect(() => {
		if (isLight && activeTab === IMAGE_REFERENCE_TYPES.PORTRAIT && textToImageStore.imageReference?.url) {
			textToImageStore.handleConfigChange("restyle_portrait", true);
			textToImageStore.handleConfigChange("prompt", "");
		} else {
			delete textToImageStore.config.restyle_portrait;
		}
	}, [activeTab, textToImageStore.imageReference?.url]);

	const handleReferenceConfigChange = (field: keyof ImageReferenceConfigsType, value: number) => {
		textToImageStore.handleConfigChange("image_reference", {
			...textToImageStore.config.image_reference,
			[field]: value,
		});
	};

	const isPortraitSelected = activeTab === IMAGE_REFERENCE_TYPES.PORTRAIT;

	const renderTabContent = (hideButtons: boolean = false) => (
		<Box className={styles.advanced}>
			<ImageUploader
				description={t("uploadImage")}
				icon={<CloudUploadSVG />}
				horizontalLayout
				largeIcon
				src={textToImageStore.imageReference?.url}
				onUpload={textToImageStore.handleUploadImage}
				onDelete={textToImageStore.handleDeleteImageReference}
			>
				{!hideButtons ? (
					<>
						<BriaButton
							className={styles.imageReferenceGallery}
							onClick={() => setIsImageGalleryOpen(!isImageGalleryOpen)}
							endIcon={<NavigateNext />}
						>
							{t("imageReferenceExamples")}
						</BriaButton>
						<BriaButton
							className={styles.imageReferenceGallery}
							onClick={() =>
								window.open(
									isPortraitSelected
										? LinkConstants.BRIA_API_TG_RESTYLE_PORTRAITS
										: LinkConstants.BRIA_API_IMAGE_REFERENCE,
									"_blank",
								)
							}
							endIcon={<ExternalLink />}
						>
							{t("goTOAPIDocs")}
						</BriaButton>
					</>
				) : (
					<></>
				)}
			</ImageUploader>
			{!hideButtons && (
				<InputLayout label={t("imageInfluence")}>
					<BriaSlider
						value={textToImageStore.config.image_reference?.structure_ref_influence}
						onChange={(_, value) => handleReferenceConfigChange("structure_ref_influence", value as number)}
						step={step}
						min={0}
						max={1}
						showMarksPoints
						marks={marks}
						valueLabelDisplay="auto"
						labelsAlwaysGray
					/>
				</InputLayout>
			)}
		</Box>
	);

	return (
		<Box className={styles.ImageReferenceContainer} ref={inputRef}>
			{!hideError && (
				<TextToImageError anchorRef={inputRef} containerRef={textToImageStore.imageGenerationContainerRef} />
			)}

			{textToImageStore.config.model !== "fibo" || isTGApp() ? (
				<Accordion
					elevation={0}
					disableGutters
					className={styles.imageReferenceAccordion}
					expanded={isImageReferenceOpen}
					onChange={() => setIsImageReferenceOpen((prev) => !prev)}
				>
					<AccordionSummary
						className={clsx(styles.AccordionSummary, { [styles.isDisabled]: isDisabled })}
						color="initial"
						expandIcon={<ExpandMore />}
					>
						{t("title")}
					</AccordionSummary>
					{isDisabled ? (
						<Box className={styles.disabledImageReference}>
							<Typography className={styles.disabledLabel}>
								{disabledMessage || t("disabledMessage")}
							</Typography>
						</Box>
					) : (
						<Box>
							<Tabs
								value={activeTab}
								onChange={(_, newValue) => setActiveTab(newValue)}
								sx={{
									"& .MuiTabs-flexContainer": { justifyContent: "space-between" },
									...((iframeStore.iframe.config.image_generation_config?.enable_restyle_portraits ||
										!iframeStore.isIframe()) && {
										borderBottom: "1px solid #E7E7E7",
									}),
								}}
							>
								{imageReferenceTabs.map((tab) => {
									const isPortraitDisabled = tab.name === IMAGE_REFERENCE_TYPES.PORTRAIT && !isLight;

									return (
										<Tab
											key={tab.name}
											className={styles.tab}
											value={tab.name}
											label={
												<Box className={styles.tabLabelContainer}>
													<InputLayout
														label={t(tab.name)}
														info={t(`tooltip.${tab.name}`, { tgProjectsLink })}
													>
														<Outlet />
													</InputLayout>
												</Box>
											}
											sx={{
												borderBottom:
													activeTab === tab.name ? "4px solid #5300C9" : "4px solid #E7E7E7",
												opacity: isPortraitDisabled ? 0.5 : 1,
												pointerEvents: isPortraitDisabled ? "none" : "auto",
											}}
											aria-disabled={isPortraitDisabled}
										/>
									);
								})}
							</Tabs>

							{renderTabContent()}
						</Box>
					)}
				</Accordion>
			) : (
				<>
					<Accordion
						elevation={0}
						disableGutters
						className={styles.imageReferenceAccordion}
						expanded={isImageReferenceOpen}
						onChange={() => setIsImageReferenceOpen((prev) => !prev)}
					>
						<AccordionSummary
							className={clsx(styles.AccordionSummary, { [styles.isDisabled]: isDisabled })}
							expandIcon={<ExpandMore />}
						>
							<Box display="flex" alignItems="center" gap={1}>
								<InputLayout label={t("imageReference")} info={t("tooltip.fiboImageReference")}>
									<Outlet />
								</InputLayout>
							</Box>
						</AccordionSummary>

						<AccordionDetails>{renderTabContent(true)}</AccordionDetails>
					</Accordion>
				</>
			)}
			{isImageGalleryOpen && (
				<ImageReferenceGallery
					onClose={() => setIsImageGalleryOpen(false)}
					activeTab={activeTab}
					imageTabs={imageReferenceTabs}
				/>
			)}
		</Box>
	);
};

export default observer(ImageReference);
